import type { components } from '@/api/schema'

type Project = components['schemas']['InternalProjectWithActionsRead']
type ProjectAction = components['schemas']['ProjectOpenActionRead']

type DataGroup = components['schemas']['ManufacturerDataActionGroupRead']

/**
 * What the table lists an item under: its project, for a Data item its manufacturer (USA-1513), or for an
 * invoice item whose invoice has no project, the "No project" group (USA-1184).
 */
export type QueueGroup = Pick<Project, 'uuid' | 'name'> & { kind?: 'manufacturer' | 'no_project' | 'mailbox' }

/** One open action item in the queue, with the project (or manufacturer) the table lists it under. */
export type QueueEntry = { action: ProjectAction; project: QueueGroup }

/** Which items the queue walks: the table's status filter (USA-1443). */
export type QueueStatus = 'not_started' | 'in_progress' | 'all'

export function entryKey(entry: QueueEntry) {
  return `${entry.action.action_source}-${entry.action.uuid}`
}

/** The table's rows in reading order: each project's items, projects in the table's order. */
export function queueEntries(projects: readonly Project[]): QueueEntry[] {
  return projects.flatMap((project) => (project.actions ?? []).map((action) => ({ action, project })))
}

const NO_PROJECT: QueueGroup = { uuid: '', name: 'No project', kind: 'no_project' }

/** The "No project" group's rows, which the table lists first (USA-1184). */
export function withoutProjectEntries(actions: readonly ProjectAction[]): QueueEntry[] {
  return actions.map((action) => ({ action, project: NO_PROJECT }))
}

const MAILBOX: QueueGroup = { uuid: '', name: "Email can't send", kind: 'mailbox' }

/**
 * The Data role's rows in reading order. Unmatched emails stay on the list, where staff match them first; a
 * mailbox our mail system refuses (USA-1729) queues first, on its own card.
 */
export function dataQueueEntries(groups: readonly DataGroup[]): QueueEntry[] {
  return groups.flatMap((group) => {
    const manufacturer = group.manufacturer
    if (!manufacturer) {
      return (group.actions ?? [])
        .filter((action) => action.action_type === 'mail_sender_blocked')
        .map((action) => ({ action, project: MAILBOX }))
    }
    const owner: QueueGroup = { uuid: manufacturer.uuid, name: manufacturer.name, kind: 'manufacturer' }
    return (group.actions ?? []).map((action) => ({ action, project: owner }))
  })
}

export function matchesStatus(entry: QueueEntry, status: QueueStatus) {
  if (status === 'all') return true
  return status === 'in_progress' ? !!entry.action.in_progress : !entry.action.in_progress
}

/**
 * The queue's walk so far. `seen` holds every item staff have opened, in order, so an item they close,
 * start or skip keeps its place: the next one is always the first listed item not yet seen.
 */
export type QueueState = { seen: QueueEntry[]; index: number }

export const EMPTY_QUEUE: QueueState = { seen: [], index: -1 }

/** Items still to open, in table order. */
export function upcoming(state: QueueState, live: readonly QueueEntry[], status: QueueStatus) {
  const seen = new Set(state.seen.map(entryKey))
  return live.filter((entry) => matchesStatus(entry, status) && !seen.has(entryKey(entry)))
}

/** Moves forward: back through items already seen, then on to the first one not yet opened. */
export function advance(state: QueueState, live: readonly QueueEntry[], status: QueueStatus): QueueState {
  if (state.index < state.seen.length - 1) return { ...state, index: state.index + 1 }
  const next = upcoming(state, live, status)[0]
  // Past the last item: the queue's end, where staff can start over.
  if (!next) return { ...state, index: state.seen.length }
  return { seen: [...state.seen, next], index: state.seen.length }
}

export function retreat(state: QueueState): QueueState {
  return { ...state, index: Math.max(0, state.index - 1) }
}

/** The current item as it is now: its live row, or undefined once it is closed. */
export function liveEntry(entry: QueueEntry, live: readonly QueueEntry[]) {
  const key = entryKey(entry)
  return live.find((candidate) => entryKey(candidate) === key)
}
